import React, { memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { Button, ButtonColor } from './Button'
import { showToast } from 'src/utils/toast'

interface Props {
  isOpen?: boolean
  title?: string
  text?: string
  submitLabel: string
  cancelLabel?: string
  color?: ButtonColor
  image?: React.ReactNode
  className?: any
  onSubmit(): void | Promise<void>
  onClose(): void
}

const SensitiveActionModalComponent = ({
  isOpen,
  title,
  text,
  submitLabel,
  cancelLabel = 'Cancel',
  image,
  className,
  color = 'red',
  onSubmit,
  onClose,
}: Props) => {
  const container = useMemo(() => document.getElementById('root'), [])

  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)

    try {
      await onSubmit()
    } catch (err) {
      showToast(err as any, { variant: 'error' })
    }

    setIsLoading(false)
  }, [onSubmit])

  return (
    <Modal
      container={container}
      open={Boolean(isOpen)}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4 z-[2001]`}
    >
      <div
        data-testid="shared-sensitive-action-modal"
        css={[tw`min-w-[340px] flex-col bg-white rounded-lg pt-6`, className]}
      >
        <div css={tw`flex justify-center`}>{image}</div>
        {title && (
          <Typography css={tw`mb-0.5 text-center`} variant="h4">
            {title}
          </Typography>
        )}
        {text && (
          <Typography css={tw`mx-4 mb-6 text-center`} color={colors.SHADES_GREY_400}>
            {text}
          </Typography>
        )}
        <div
          css={tw`flex justify-between items-center px-4 py-3 border-0 border-t border-solid  border-[#EDEDED]`}
        >
          <Button
            className={tw`px-8`}
            disabled={isLoading}
            onClick={onClose}
            color="grey"
            size="small"
            data-testid="sensitive-action-modal-cancel-btn"
          >
            {cancelLabel}
          </Button>
          <Button
            className={tw`px-8`}
            loading={isLoading}
            onClick={handleSubmit}
            color={color}
            size="small"
            data-testid="sensitive-action-modal-submit-btn"
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const SensitiveActionModal = memo(SensitiveActionModalComponent)
